import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery } from '../QueryContext';
import apiClient from '../api/client';
import { motion } from 'framer-motion';
import './Daily.css';
import {
  Download, Calendar, Search, RefreshCw,
  Database, ChevronDown, FileText
} from 'lucide-react';

const nombresMeses = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

const FacturacionMenuHeader = () => null;

// ==========================================
// COMPONENTE: MultiSelectFilter
// ==========================================
const MultiSelectFilter = ({ label, name, icon: Icon, options = [], selectedValues, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const totalOptions = options.length;
  const hasSelection = selectedValues?.length > 0;
  const allOptionsSelected = totalOptions > 0 && selectedValues.length === totalOptions;

  const displayValue = allOptionsSelected
    ? 'Todos'
    : hasSelection
      ? (selectedValues.length === 1
        ? options.find(opt => (opt.value || opt) === selectedValues[0])?.label || selectedValues[0]
        : `${selectedValues.length} Selec.`)
      : 'Todos';

  const showSearch = options.length > 12;
  const filteredOptions = options.filter(opt =>
    String(opt.label || opt).toLowerCase().includes(searchTerm.toLowerCase())
  ).slice(0, 100);

  const areAllSelected = filteredOptions.every(opt =>
    selectedValues.includes(opt.value || opt)
  );

  const handleSelectAll = () => {
    if (areAllSelected) {
      const newSelected = selectedValues.filter(val => !filteredOptions.some(opt => (opt.value || opt) === val));
      onChange(name, newSelected);
    } else {
      const newValuesOnly = filteredOptions.map(opt => opt.value || opt);
      onChange(name, newValuesOnly);
    }
  };

  return (
    <div className="filter-pill-container" ref={dropdownRef}>
      <button className={`filter-pill-button ${hasSelection ? 'active' : ''}`} onClick={() => setIsOpen(!isOpen)}>
        <Icon size={12} className="pill-icon" />
        <span className="pill-label">{label}:</span>
        <span className="pill-value">{displayValue}</span>
        <ChevronDown size={11} className={`pill-arrow ${isOpen ? 'up' : ''}`} />
      </button>
      {isOpen && (
        <div className={`filter-dropdown-panel ${showSearch ? 'with-search' : ''}`}>
          {showSearch && (
            <div className="search-wrapper">
              <Search size={14} className="search-icon" />
              <input type="text" placeholder="Buscar..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} autoFocus />
            </div>
          )}
          {filteredOptions.length > 0 && (
            <label className="checkbox-row select-all-row">
              <input type="checkbox" checked={areAllSelected} onChange={handleSelectAll} />
              <span>Seleccionar Todos ({filteredOptions.length})</span>
            </label>
          )}
          <div className="options-list">
            {filteredOptions.map((opt, i) => {
              const optValue = opt.value || opt;
              const optLabel = opt.label || opt;
              return (
                <label key={`${name}-${optValue}-${i}`} className="checkbox-row">
                  <input type="checkbox" checked={selectedValues.includes(optValue)} onChange={() => onChange(name, selectedValues.includes(optValue) ? selectedValues.filter(v => v !== optValue) : [...selectedValues, optValue])} />
                  <span>{optLabel}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// ==========================================
// COMPONENTE PRINCIPAL: ReportesAV
// ==========================================
const ReportesAV = () => {
  const { getSalesFilters } = useQuery();

  // Control de Cargas y Filtros
  const [loading, setLoading] = useState(false);
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedMeses, setSelectedMeses] = useState([]);
  const [selectedAnios, setSelectedAnios] = useState([]);

  // Estados de la Tabla
  const [tableHeaders, setTableHeaders] = useState([]);
  const [rawRowsData, setRawRowsData] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(30);

  // Formateadores estándar
  const formatNum = (v) => Number(v || 0).toLocaleString('es-VE', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  const formatUSD = (v) => '$' + Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  useEffect(() => {
    const loadConfigBase = async () => {
      try {
        const config = await getSalesFilters();
        if (config) {
          const currentYear = new Date().getFullYear();
          setAvailableYears(config.availableYears || [currentYear]);
          setSelectedAnios([config.currentYear || currentYear]);
          setSelectedMeses([nombresMeses[new Date().getMonth()]]);
        }
      } catch (err) {
        console.error("Error cargando filtros base:", err);
      }
    };
    loadConfigBase();
  }, [getSalesFilters]);

  const handleFetchRawData = useCallback(async () => {
    setLoading(true);
    try {
      const mesesNumeros = selectedMeses.length === 0 ? nombresMeses.map((_, i) => i + 1) : selectedMeses.map(mes => nombresMeses.indexOf(mes) + 1);
      const aniosAEnviar = selectedAnios.length === 0 ? availableYears : selectedAnios;

      const queryPayload = {
        page_meses: mesesNumeros,
        page_anios: aniosAEnviar,
        comparison_mode: "CUOTA",
        view_type: "mensual",
        filters: {},
        user_info: null
      };

      const response = await apiClient.post('/salesv2/execute/reporte-completo', queryPayload);

      if (!response.data || !response.data.reporte_completo_table) {
        setRawRowsData([]);
        return;
      }

      const backendRows = response.data.reporte_completo_table;

      setTableHeaders([
        "Periodo", "Fecha", "Factura", "Factura Anulada", "Cliente ID", "Cliente Detalle", "Cliente Inactivo",
        "Segmento", "Cliente", "Vendedor", "Canal", "Canal 2", "Región Filtro", "Territorio",
        "SKU ID", "Producto", "Casas", "Marca", "Línea", "Clasificación", "Portafolio",
        "Unidades", "Bultos Reales", "Cuota Bultos", "Forecast Bultos", "Bruto USD", "Cuota USD", "Forecast USD"
      ]);

      const processedFlatRows = backendRows.map(row => {
        let fechaFactura = "S/F";
        if (row.Fecha) fechaFactura = row.Fecha.split('T')[0];
        else if (row.V_FECHA) fechaFactura = row.V_FECHA.split('T')[0];

        return {
          periodo: row.Periodo || "S/P",
          fecha: fechaFactura,
          factura: row.V_FACTURA || row.Factura || "S/F",
          vAnulada: row.V_ANULADA || "NO",
          clienteId: row.CLIENTE_ID || row.Cliente_ID || "S/C",
          clienteNombre: row.CLI_NOMBRE || row.Cli_Nombre || "Sin Cliente",
          cliInactivo: row.CLI_INACTIVO || "NO",
          cliSegDes: row.CLI_SEG_DES || "S/S",
          cliPrin: row.CLI_PRIN || "S/P",
          cliVenDes: row.CLI_VEN_DES || row.Vendedor || "S/V",
          cliCanal: row.CLI_CANAL || "S/C",
          cliCanalReal: row.CLI_CANAL_REAL || "S/CR",
          cliRegVerd: row.CLI_REG_VERD || row.Region || "S/R",
          cliTerritorio: row.CLI_TERRITORIO || row.Territorio || "S/T",
          sku: row.ART_ID || row.Art_ID || "S/SKU",
          productoDesc: row.BI_DESC1 || row.Bi_Desc1 || "Sin Descripción",
          biProveedor: row.BI_PROVEEDOR || row.Proveedor || "S/PR",
          biMarca: row.BI_MARCA || row.Marca || "S/M",
          biLinea: row.BI_LINEA || "S/L",
          biClasif2: row.BI_CLASIF2 || "S/C2",
          biSegmento: row.BI_SEGMENTO || "S/SEG",

          unidades: Number(row.Unidades || 0),
          bultos: Number(row.Bultos || 0),
          cuotaBultos: Number(row.Cuota_Bultos || 0),
          forecastBultos: Number(row.Forecast_Bultos || 0),
          brutoUsd: Number(row.Bruto_USD || 0),
          cuotaUsd: Number(row.Cuota_USD || 0),
          forecastUsd: Number(row.Forecast_USD || 0)
        };
      });

      setRawRowsData(processedFlatRows);

    } catch (err) {
      console.error("Error al obtener registros crudos:", err);
    } finally {
      setLoading(false);
    }
  }, [selectedMeses, selectedAnios, availableYears]);

  useEffect(() => {
    if (selectedAnios.length > 0) {
      handleFetchRawData();
    }
  }, [handleFetchRawData, selectedAnios]);

  const handleApplyFilters = (page) => {
    setCurrentPage(page);
  };

  const filteredRows = rawRowsData.filter(row =>
    row.factura.toLowerCase().includes(searchValue.toLowerCase()) ||
    row.clienteId.toLowerCase().includes(searchValue.toLowerCase()) ||
    row.clienteNombre.toLowerCase().includes(searchValue.toLowerCase()) ||
    row.sku.toLowerCase().includes(searchValue.toLowerCase()) ||
    row.productoDesc.toLowerCase().includes(searchValue.toLowerCase()) ||
    row.cliVenDes.toLowerCase().includes(searchValue.toLowerCase()) ||
    row.biMarca.toLowerCase().includes(searchValue.toLowerCase())
  );

  const totalCount = filteredRows.length;
  const totalPages = Math.ceil(totalCount / rowsPerPage);
  const pagedRows = filteredRows.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);
  const pageTotalUSD = pagedRows.reduce((acc, r) => acc + r.brutoUsd, 0);

  // EXPORTACIÓN 1: Detallada completa
  const handleExportCSVCompleto = () => {
    if (rawRowsData.length === 0) return;
    const headersCSV = [
      "Periodo", "Fecha", "Factura", "Factura Anulada", "Cliente ID", "Cliente Detalle", "Cliente Inactivo",
      "Segmento", "Cliente", "Vendedor", "Canal", "Canal 2", "Región Filtro", "Territorio",
      "SKU ID", "Producto", "Casas", "Marca", "Línea", "Clasificación", "Portafolio",
      "Unidades", "Bultos Reales", "Cuota Bultos", "Forecast Bultos", "Bruto USD", "Cuota USD", "Forecast USD"
    ];
    
    let csvContent = "\uFEFF" + headersCSV.join(";") + "\n";
    
    rawRowsData.forEach(f => {
      const line = [
        `"${f.periodo}"`, `"${f.fecha}"`, `"${f.factura}"`, `"${f.vAnulada}"`, `"${f.clienteId}"`,
        `"${f.clienteNombre.replace(/"/g, '""')}"`, `"${f.cliInactivo}"`, `"${f.cliSegDes.replace(/"/g, '""')}"`,
        `"${f.cliPrin}"`, `"${f.cliVenDes.replace(/"/g, '""')}"`, `"${f.cliCanal}"`, `"${f.cliCanalReal}"`,
        `"${f.cliRegVerd}"`, `"${f.cliTerritorio}"`, `"${f.sku}"`, `"${f.productoDesc.replace(/"/g, '""')}"`,
        `"${f.biProveedor.replace(/"/g, '""')}"`, `"${f.biMarca.replace(/"/g, '""')}"`, `"${f.biLinea}"`,
        `"${f.biClasif2}"`, `"${f.biSegmento}"`,
        f.unidades.toFixed(2), f.bultos.toFixed(2), f.cuotaBultos.toFixed(2), f.forecastBultos.toFixed(2),
        f.brutoUsd.toFixed(2), f.cuotaUsd.toFixed(2), f.forecastUsd.toFixed(2)
      ];
      csvContent += line.join(";") + "\n";
    });
    descargarBlob(csvContent, "Reporte_Granular_Ventas_Completo.csv");
  };

  // 📊 EXPORTACIÓN 2: Resumida Mensual (¡Manteniendo "Factura Anulada"!)
  const handleExportCSVArupadoMensual = () => {
    if (rawRowsData.length === 0) return;

    const mapAgrupado = {};

    const limpiar = (val) => String(val ?? '').trim().toUpperCase();

    rawRowsData.forEach(f => {
      let anio = "S/A";
      let mes = "S/M";

      if (f.periodo) {
        const partesPeriodo = String(f.periodo).split('/');
        if (partesPeriodo.length === 2) {
          mes = partesPeriodo[0].trim().toUpperCase();
          anio = partesPeriodo[1].trim();
        } else {
          mes = String(f.periodo).trim();
        }
      }

      // 🚨 QUITAMOS f.vAnulada de la llave compuesta para que NO duplique filas
      const llaveCompuesta = [
        limpiar(anio), 
        limpiar(mes), 
        limpiar(f.clienteId), 
        limpiar(f.clienteNombre), 
        limpiar(f.cliInactivo),
        limpiar(f.cliSegDes), 
        limpiar(f.cliPrin), 
        limpiar(f.cliVenDes), 
        limpiar(f.cliCanal), 
        limpiar(f.cliCanalReal),
        limpiar(f.cliRegVerd), 
        limpiar(f.cliTerritorio), 
        limpiar(f.sku), 
        limpiar(f.productoDesc), 
        limpiar(f.biProveedor),
        limpiar(f.biMarca), 
        limpiar(f.biLinea), 
        limpiar(f.biClasif2), 
        limpiar(f.biSegmento)
      ].join('|||');

      if (!mapAgrupado[llaveCompuesta]) {
        mapAgrupado[llaveCompuesta] = {
          anio: anio,
          mes: mes,
          vAnulada: "NO", // Por defecto asumimos NO, o lo manejamos abajo
          clienteId: String(f.clienteId ?? '').trim(),
          clienteNombre: String(f.clienteNombre ?? '').trim(),
          cliInactivo: String(f.cliInactivo ?? '').trim(),
          cliSegDes: String(f.cliSegDes ?? '').trim(),
          cliPrin: String(f.cliPrin ?? '').trim(),
          cliVenDes: String(f.cliVenDes ?? '').trim(),
          cliCanal: String(f.cliCanal ?? '').trim(),
          cliCanalReal: String(f.cliCanalReal ?? '').trim(),
          cliRegVerd: String(f.cliRegVerd ?? '').trim(),
          cliTerritorio: String(f.cliTerritorio ?? '').trim(),
          sku: String(f.sku ?? '').trim(),
          productoDesc: String(f.productoDesc ?? '').trim(),
          biProveedor: String(f.biProveedor ?? '').trim(),
          biMarca: String(f.biMarca ?? '').trim(),
          biLinea: String(f.biLinea ?? '').trim(),
          biClasif2: String(f.biClasif2 ?? '').trim(),
          biSegmento: String(f.biSegmento ?? '').trim(),
          unidades: 0,
          bultos: 0,
          cuotaBultos: 0,
          forecastBultos: 0,
          brutoUsd: 0,
          cuotaUsd: 0,
          forecastUsd: 0
        };
      }

      // Si detectamos que esta fila original era una anulada, podemos dejar marca en el registro agrupado si quieres,
      // pero lo correcto para el reporte pesado es acumular los valores netos:
      mapAgrupado[llaveCompuesta].unidades += Number(f.unidades || 0);
      mapAgrupado[llaveCompuesta].bultos += Number(f.bultos || 0);
      mapAgrupado[llaveCompuesta].cuotaBultos += Number(f.cuotaBultos || 0);
      mapAgrupado[llaveCompuesta].forecastBultos += Number(f.forecastBultos || 0);
      mapAgrupado[llaveCompuesta].brutoUsd += Number(f.brutoUsd || 0);
      mapAgrupado[llaveCompuesta].cuotaUsd += Number(f.cuotaUsd || 0);
      mapAgrupado[llaveCompuesta].forecastUsd += Number(f.forecastUsd || 0);
      
      // Si el neto acumulado de bultos o dólares termina siendo cero, sabrás que se anuló por completo
      if (mapAgrupado[llaveCompuesta].brutoUsd <= 0 && mapAgrupado[llaveCompuesta].unidades <= 0) {
         mapAgrupado[llaveCompuesta].vAnulada = "SI";
      }
    });

    // Cabeceras del CSV
    const headersCSV = [
      "Año", "Mes", "Factura Anulada", "Cliente ID", "Cliente Detalle", "Cliente Inactivo",
      "Segmento", "Cliente", "Vendedor", "Canal", "Canal 2", "Región Filtro", "Territorio",
      "SKU ID", "Producto", "Casas", "Marca", "Línea", "Clasificación", "Portafolio",
      "Unidades", "Bultos Reales", "Cuota Bultos", "Forecast Bultos", "Bruto USD", "Cuota USD", "Forecast USD"
    ];

    let csvContent = "\uFEFF" + headersCSV.join(";") + "\n";

    Object.values(mapAgrupado).forEach(item => {
      const line = [
        `"${item.anio}"`, `"${item.mes}"`, `"${item.vAnulada}"`, `"${item.clienteId}"`,
        `"${item.clienteNombre.replace(/"/g, '""')}"`, `"${item.cliInactivo}"`, `"${item.cliSegDes.replace(/"/g, '""')}"`,
        `"${item.cliPrin}"`, `"${item.cliVenDes.replace(/"/g, '""')}"`, `"${item.cliCanal}"`, `"${item.cliCanalReal}"`,
        `"${item.cliRegVerd}"`, `"${item.cliTerritorio}"`, `"${item.sku}"`, `"${item.productoDesc.replace(/"/g, '""')}"`,
        `"${item.biProveedor.replace(/"/g, '""')}"`, `"${item.biMarca.replace(/"/g, '""')}"`, `"${item.biLinea}"`,
        `"${item.biClasif2}"`, `"${item.biSegmento}"`,
        item.unidades.toFixed(2), item.bultos.toFixed(2), item.cuotaBultos.toFixed(2), item.forecastBultos.toFixed(2),
        item.brutoUsd.toFixed(2), item.cuotaUsd.toFixed(2), item.forecastUsd.toFixed(2)
      ];
      csvContent += line.join(";") + "\n";
    });

    descargarBlob(csvContent, "Reporte_Resumen_Mensual_Agrupado.csv");
};

  const descargarBlob = (content, filename) => {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <motion.div className="dashboard-container" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <FacturacionMenuHeader />

      <div style={{ marginTop: '20px' }}>
        <header className="header-flex">
          <div className="title-section">
            <h1 className="dashboard-title">Detalle de Ventas e Indicadores</h1>
            <div className="title-underline"></div>
          </div>

          <div className="date-selectors">
            <button className="refresh-button" onClick={() => handleFetchRawData()} title="Actualizar datos">
              <RefreshCw size={16} className={loading ? "spin-anim" : ""} />
            </button>
            <button className="refresh-button" onClick={handleExportCSVCompleto} title="Exportar CSV Granular" style={{ background: '#475569' }}>
              <Download size={16} />
            </button>

            <div className="filters-and-search-group">
              <div className="date-filters-group">
                <MultiSelectFilter label="Mes" name="month" icon={Calendar} options={nombresMeses} selectedValues={selectedMeses} onChange={(_, v) => setSelectedMeses(v)} />
                <MultiSelectFilter label="Año" name="year" icon={Calendar} options={availableYears} selectedValues={selectedAnios} onChange={(_, v) => setSelectedAnios(v)} />
              </div>

              <div className="global-search-container">
                <div className="global-search-input-wrapper">
                  <input
                    type="text"
                    placeholder="Buscar por factura, SKU, producto..."
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    className="global-search-input"
                  />
                </div>
              </div>
            </div>
          </div>
        </header>

        <div className="detail-table-container">
          <div className="table-header">
            <h3>Registros de Ventas Prorrateados</h3>
            <div className="table-controls">
              <button
                onClick={handleExportCSVArupadoMensual}
                className="export-agrupado-btn"
                style={{
                  marginRight: '15px',
                  background: '#0284c7',
                  color: '#fff',
                  border: 'none',
                  padding: '6px 12px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '0.85rem',
                  fontWeight: '500'
                }}
              >
                <FileText size={14} /> Exportar Agrupado Mensual
              </button>

              <span>Total líneas: {totalCount}</span>
              <div className="pagination-controls">
                <button onClick={() => handleApplyFilters(Math.max(1, currentPage - 1))} disabled={currentPage === 1}>Anterior</button>
                <span>Página {currentPage} de {totalPages || 1}</span>
                <button onClick={() => handleApplyFilters(currentPage + 1)} disabled={currentPage >= (totalPages || 1)}>Siguiente</button>
              </div>
            </div>
          </div>

          {pagedRows.length === 0 ? (
            <div style={{ padding: '50px', textAlign: 'center', opacity: 0.5 }}>
              No hay registros disponibles.
            </div>
          ) : (
            <>
              <div className="responsive-table-wrapper" style={{ maxHeight: '62vh', overflow: 'auto' }}>
                <table className="detail-table">
                  <thead>
                    <tr>
                      {tableHeaders.map((header, index) => (
                        <th key={index} style={{ whiteSpace: 'nowrap', textAlign: header.includes('USD') || header.includes('Unidades') || header.includes('Bultos') || header.includes('Cuota') || header.includes('Forecast') ? 'right' : 'left' }}>
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pagedRows.map((row, index) => (
                      <tr key={index}>
                        <td>{row.periodo}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{row.fecha}</td>
                        <td><strong>{row.factura}</strong></td>
                        <td>{row.vAnulada}</td>
                        <td>{row.clienteId}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{row.clienteNombre}</td>
                        <td>{row.cliInactivo}</td>
                        <td>{row.cliSegDes}</td>
                        <td>{row.cliPrin}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{row.cliVenDes}</td>
                        <td>{row.cliCanal}</td>
                        <td>{row.cliCanalReal}</td>
                        <td>{row.cliRegVerd}</td>
                        <td>{row.cliTerritorio}</td>
                        <td><code>{row.sku}</code></td>
                        <td style={{ minWidth: '200px' }}>{row.productoDesc}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{row.biProveedor}</td>
                        <td>{row.biMarca}</td>
                        <td>{row.biLinea}</td>
                        <td>{row.biClasif2}</td>
                        <td>{row.biSegmento}</td>

                        <td style={{ textAlign: 'right' }}>{formatNum(row.unidades)}</td>
                        <td style={{ textAlign: 'right' }}>{formatNum(row.bultos)}</td>
                        <td style={{ textAlign: 'right' }}>{formatNum(row.cuotaBultos)}</td>
                        <td style={{ textAlign: 'right' }}>{formatNum(row.forecastBultos)}</td>
                        <td style={{ textAlign: 'right' }}>{formatUSD(row.brutoUsd)}</td>
                        <td style={{ textAlign: 'right' }}>{formatUSD(row.cuotaUsd)}</td>
                        <td style={{ textAlign: 'right' }}>{formatUSD(row.forecastUsd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="table-footer-pagination" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px', background: 'rgba(0,0,0,0.2)' }}>
                <div style={{ display: 'flex', gap: '20px' }}>
                  <span><strong>{formatNum(totalCount)}</strong> registros en vista</span>
                  <span>Venta Real Pág: <strong>{formatUSD(pageTotalUSD)}</strong></span>
                </div>
                <div style={{ opacity: 0.6, fontSize: '0.75rem' }}>
                  <Database size={12} style={{ display: 'inline', marginRight: '4px' }} /> DuckDB Data Engine
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default ReportesAV;